import type { ChangeEvent } from "react";

import { apiClient, type ReportType } from "../../api/client";
import { reportLabels, reportTypes, type FlowState } from "./types";

interface ImportStepProps {
  state: FlowState;
  onFile: (reportType: ReportType, file: File | undefined) => void;
  onSource: (sourceId: string) => void;
  onProfile: (reportType: ReportType, profileId: string) => void;
  onPeriod: (field: "periodStart" | "periodEnd", value: string) => void;
  onComparisonWindow: (weeks: number) => void;
  onReportingCurrency: (currency: string) => void;
  onCurrencyRates: (rates: string) => void;
  onVariant: (reportType: ReportType, variant: string) => void;
  onSubmit: () => void;
}

export function ImportStep({
  state,
  onFile,
  onSource,
  onProfile,
  onPeriod,
  onComparisonWindow,
  onReportingCurrency,
  onCurrencyRates,
  onVariant,
  onSubmit,
}: ImportStepProps) {
  return (
    <section className="flow-panel" aria-labelledby="import-title">
      <div className="panel-heading">
        <div>
          <span className="eyebrow">第 1 步</span>
          <h2 id="import-title">导入三份 Amazon 报表</h2>
        </div>
        <span className="phase-badge">已发布配置 · 可选来源变体</span>
      </div>
      <p className="panel-copy">
        选择已授权店铺、报告窗口和来源变体。三份文件必须覆盖同一范围，并满足对应已发布配置。
      </p>
      <div className="import-config-grid">
        <label>
          店铺数据源
          <select onChange={(event) => onSource(event.target.value)} value={state.selectedSourceId}>
            <option value="">请选择</option>
            {state.sources.map((source) => (
              <option key={source.id} value={source.id}>
                {source.display_name}
              </option>
            ))}
          </select>
        </label>
        <label>
          开始日期
          <input
            onChange={(event) => onPeriod("periodStart", event.target.value)}
            type="date"
            value={state.periodStart}
          />
        </label>
        <label>
          结束日期
          <input
            onChange={(event) => onPeriod("periodEnd", event.target.value)}
            type="date"
            value={state.periodEnd}
          />
        </label>
        <label>
          对比窗口（周）
          <input
            max={12}
            min={1}
            onChange={(event) => onComparisonWindow(Number(event.target.value))}
            type="number"
            value={state.comparisonWindowWeeks}
          />
        </label>
        <label>
          报告币种
          <input
            maxLength={3}
            onChange={(event) => onReportingCurrency(event.target.value.toUpperCase())}
            value={state.reportingCurrency}
          />
        </label>
        <label>
          源币种汇率（JSON）
          <input
            onChange={(event) => onCurrencyRates(event.target.value)}
            value={state.currencyRates}
          />
        </label>
      </div>
      <div className="upload-grid">
        {reportTypes.map((reportType) => {
          const profiles = state.profiles.filter((item) => item.report_type === reportType);
          const profile =
            profiles.find((item) => item.id === state.selectedProfileIds[reportType]) ??
            profiles[0];
          const file = state.files[reportType];
          return (
            <article className={file ? "upload-card complete" : "upload-card"} key={reportType}>
              <span className="upload-index">{reportTypes.indexOf(reportType) + 1}</span>
              <h3>{reportLabels[reportType]}</h3>
              <p>{profile?.display_name ?? "正在加载已发布模板…"}</p>
              {profile && (
                <>
                  <label className="variant-picker">
                    导入配置
                    <select
                      aria-label={`${reportLabels[reportType]}导入配置`}
                      onChange={(event) => onProfile(reportType, event.target.value)}
                      value={profile.id}
                    >
                      {profiles.map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.display_name} · v{item.version}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="variant-picker">
                    来源变体
                    <select
                      aria-label={`${reportLabels[reportType]}来源变体`}
                      onChange={(event) => onVariant(reportType, event.target.value)}
                      value={
                        state.sourceVariants[reportType] ??
                        profile.definition.default_source_variant
                      }
                    >
                      {profile.definition.source_variants.map((variant) => (
                        <option key={variant.code} value={variant.code}>
                          {variant.code}
                        </option>
                      ))}
                    </select>
                  </label>
                </>
              )}
              {profile?.sample_filename && (
                <a href={apiClient.profileSampleUrl(profile.id)}>下载模板样例</a>
              )}
              <label className="file-picker">
                <span>{file ? file.name : "选择 CSV 或 XLSX"}</span>
                <input
                  accept=".csv,.xlsx"
                  aria-label={`选择${reportLabels[reportType]}文件`}
                  onChange={(event: ChangeEvent<HTMLInputElement>) =>
                    onFile(reportType, event.target.files?.[0])
                  }
                  type="file"
                />
              </label>
            </article>
          );
        })}
      </div>
      {state.error && (
        <div className="alert error" role="alert">
          {state.error}
        </div>
      )}
      <div className="flow-actions">
        <span>{reportTypes.filter((item) => state.files[item]).length} / 3 份文件已选择</span>
        <button disabled={state.busy} onClick={onSubmit} type="button">
          {state.busy ? "正在上传并校验…" : "上传并校验"}
        </button>
      </div>
    </section>
  );
}
